import { TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE } from '../constants';
import {
  GetSelectOptionByIdFunction,
  SearchOptions,
  SelectOptionSearchFunction,
  TypesenseCollection,
} from '../types';
import { searchCollection, TypesenseDocument } from '../utils';
import { ProjectSubcontractOption } from './projectSubcontracts.types';

const BASE_FILTER_ACTIVE = 'Status:[1..2]';

export const getSearchProjectSubcontractSelectOptions: SelectOptionSearchFunction<
  ProjectSubcontractOption
> = async (
  searchValue: string,
  apiKey: string,
  pageIndex: number = 1,
  options: SearchOptions = {},
) => {
  const results = await searchCollection(
    TypesenseCollection.PROJECT_SUBCONTRACTS.name,
    'Name',
    searchValue,
    apiKey,
    pageIndex,
    TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE,
    options.filterBy
      ? `${options.filterBy} && ${BASE_FILTER_ACTIVE}`
      : BASE_FILTER_ACTIVE,
    options.sortBy,
  );
  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch projectSubcontracts from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: item.Name,
    id: item.SourceId.toString(),
    isBillable: item.IsBillable,
    isDefaultExpenses: item.IsDefaultExpenses,
    isFixedPriceContract: item.InvoicingType === 1,
    isActive: item.Status === 1 || item.Status === 2,
  }));
  return mappedResults ?? [];
};

/**
 * Retrieves a projectSubcontract select option by ID from Typesense.
 * @throws {Error} When the search fails, returns an error, or no projectSubcontract is found with the given ID
 */
export const getProjectSubcontractSelectOptionById: GetSelectOptionByIdFunction<
  ProjectSubcontractOption
> = async (
  id: string,
  apiKey: string,
  additionalFilterOptions?: SearchOptions['filterBy'],
) => {
  const results = await searchCollection(
    TypesenseCollection.PROJECT_SUBCONTRACTS.name,
    'Name',
    '*',
    apiKey,
    1,
    1,
    additionalFilterOptions
      ? `${additionalFilterOptions} && SourceId:=${id}`
      : `SourceId:=${id}`,
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error ||
        'Failed to fetch projectSubcontract by id from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: item.Name,
    id: item.SourceId.toString(),
    isBillable: item.IsBillable,
    isDefaultExpenses: item.IsDefaultExpenses,
    isFixedPriceContract: item.InvoicingType === 1,
    isActive: item.Status === 1 || item.Status === 2,
  }));

  if (!mappedResults || mappedResults.length === 0) {
    //  debug only here as we just throw an error to be able to use try-catch, but it is not necessarily an actual error
    console.debug(`ProjectSubcontract with id ${id} not found`);
    throw new Error(`ProjectSubcontract with id ${id} not found`);
  }

  return mappedResults[0];
};
